import React, { useState, useEffect } from 'react';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiUser, FiPhone, FiMail } from 'react-icons/fi';
import { toast } from 'react-toastify';
import axios from 'axios';
import styles from './PacientesNew.module.css';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

const Pacientes = () => {
  const { hasPermission, getPermissionScope } = useAuth();
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPaciente, setEditingPaciente] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    data_nascimento: '',
    telefone: '',
    email: '',
    endereco: '',
    cep: '',
    cidade: '',
    estado: '',
    genero: 'masculino',
    estado_civil: 'solteiro'
  });

  const fetchPacientes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/medical/pacientes');
      if (response.data.success) {
        setPacientes(response.data.data.pacientes || []);
      }
    } catch (error) {
      toast.error('Erro ao carregar pacientes');
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPacientes();
  }, []);

  // Verificar permissões - movido para depois dos hooks
  const hasAnyPacientePermission = hasPermission('cadastros:create_pacientes') || hasPermission('cadastros:edit_pacientes');
  
  if (!hasAnyPacientePermission) {
    return (
      <div className={styles.noPermission}>
        <h2>Acesso Negado</h2>
        <p>Você não tem permissão para acessar a gestão de pacientes.</p>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPaciente) {
        await axios.put(`/medical/pacientes/${editingPaciente.id}`, formData);
        toast.success('Paciente atualizado com sucesso!');
      } else {
        await axios.post('/medical/pacientes', formData);
        toast.success('Paciente cadastrado com sucesso!');
      }
      
      resetForm();
      fetchPacientes();
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao salvar paciente';
      toast.error(message);
    }
  };

  const handleEdit = (paciente) => {
    setEditingPaciente(paciente);
    setFormData({
      nome: paciente.nome || '',
      cpf: paciente.cpf || '',
      data_nascimento: paciente.data_nascimento || '',
      telefone: paciente.telefone || '',
      email: paciente.email || '',
      endereco: paciente.endereco || '',
      cep: paciente.cep || '',
      cidade: paciente.cidade || '',
      estado: paciente.estado || '',
      genero: paciente.genero || 'masculino',
      estado_civil: paciente.estado_civil || 'solteiro'
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este paciente?')) {
      try {
        await axios.delete(`/medical/pacientes/${id}`);
        toast.success('Paciente excluído com sucesso!');
        fetchPacientes();
      } catch (error) {
        const message = error.response?.data?.message || 'Erro ao excluir paciente';
        toast.error(message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      cpf: '',
      data_nascimento: '',
      telefone: '',
      email: '',
      endereco: '',
      cep: '',
      cidade: '',
      estado: '',
      genero: 'masculino',
      estado_civil: 'solteiro'
    });
    setEditingPaciente(null);
    setShowForm(false);
  };

  const filteredPacientes = pacientes.filter(paciente =>
    paciente.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    paciente.cpf?.includes(searchTerm) ||
    paciente.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  // Verificar se pode criar/editar/excluir
  const canCreate = hasPermission('cadastros:create_pacientes') && getPermissionScope('cadastros:create_pacientes') !== 'none';
  const canEdit = hasPermission('cadastros:edit_pacientes') && getPermissionScope('cadastros:edit_pacientes') !== 'none';
  const canDelete = hasPermission('cadastros:edit_pacientes') && getPermissionScope('cadastros:edit_pacientes') !== 'none';

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Gestão de Pacientes</h1>
        <p>Gerencie o cadastro e informações dos pacientes</p>
      </div>

      <div className={styles.actions}>
        <div className="formInputWrapper" style={{ flex: 1, maxWidth: '400px' }}>
          <FiSearch className="formInputIcon" />
          <input
            type="text"
            placeholder="Buscar por nome, CPF ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="formInput"
          />
        </div>
        
        {canCreate && (
          <button
            onClick={() => setShowForm(true)}
            className="formButton primary"
          >
            <FiPlus /> Novo Paciente
          </button>
        )}
      </div>

      {showForm && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>{editingPaciente ? 'Editar Paciente' : 'Novo Paciente'}</h2>
              <button onClick={resetForm} className={styles.closeButton}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="form">
              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel">Nome Completo</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      className="formInput"
                      placeholder="Digite o nome completo"
                      required
                    />
                  </div>
                </div>
                
                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel">CPF</label>
                    <input
                      type="text"
                      name="cpf"
                      value={formData.cpf}
                      onChange={handleInputChange}
                      className="formInput"
                      placeholder="000.000.000-00"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Data de Nascimento</label>
                    <input
                      type="date"
                      name="data_nascimento"
                      value={formData.data_nascimento}
                      onChange={handleInputChange}
                      className="formInput"
                    />
                  </div>
                </div>
                
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Gênero</label>
                    <select
                      name="genero"
                      value={formData.genero}
                      onChange={handleInputChange}
                      className="formSelect"
                    >
                      <option value="masculino">Masculino</option>
                      <option value="feminino">Feminino</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Telefone</label>
                    <div className="formInputWrapper">
                      <FiPhone className="formInputIcon" />
                      <input
                        type="tel"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleInputChange}
                        className="formInput"
                        placeholder="(11) 99999-9999"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Email</label>
                    <div className="formInputWrapper">
                      <FiMail className="formInputIcon" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="formInput"
                        placeholder="email@exemplo.com"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol narrow">
                  <div className="formGroup">
                    <label className="formLabel">CEP</label>
                    <input
                      type="text"
                      name="cep"
                      value={formData.cep}
                      onChange={handleInputChange}
                      className="formInput"
                      placeholder="00000-000"
                    />
                  </div>
                </div>
                
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Estado Civil</label>
                    <select
                      name="estado_civil"
                      value={formData.estado_civil}
                      onChange={handleInputChange}
                      className="formSelect"
                    >
                      <option value="solteiro">Solteiro(a)</option>
                      <option value="casado">Casado(a)</option>
                      <option value="divorciado">Divorciado(a)</option>
                      <option value="viuvo">Viúvo(a)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="formGroup">
                <label className="formLabel">Endereço</label>
                <input
                  type="text"
                  name="endereco"
                  value={formData.endereco}
                  onChange={handleInputChange}
                  className="formInput"
                  placeholder="Rua, número, bairro"
                />
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Cidade</label>
                    <input
                      type="text"
                      name="cidade"
                      value={formData.cidade}
                      onChange={handleInputChange}
                      className="formInput"
                      placeholder="Digite a cidade"
                    />
                  </div>
                </div>
                
                <div className="formCol narrow">
                  <div className="formGroup">
                    <label className="formLabel">Estado</label>
                    <input
                      type="text"
                      name="estado"
                      value={formData.estado}
                      onChange={handleInputChange}
                      className="formInput"
                      maxLength="2"
                      placeholder="SP"
                    />
                  </div>
                </div>
              </div>

              <div className="formButtonGroup right">
                <button type="button" onClick={resetForm} className="formButton secondary">
                  Cancelar
                </button>
                <button type="submit" className="formButton primary">
                  {editingPaciente ? 'Atualizar' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className={styles.tableContainer}>
        {filteredPacientes.length > 0 ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nome</th>
                <th>CPF</th>
                <th>Telefone</th>
                <th>Email</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredPacientes.map((paciente) => (
                <tr key={paciente.id}>
                  <td>
                    <div className={styles.patientInfo}>
                      <FiUser className={styles.patientIcon} />
                      <div>
                        <div className={styles.patientName}>{paciente.nome}</div>
                        <div className={styles.patientDetails}>{paciente.genero} • {paciente.estado_civil}</div>
                      </div>
                    </div>
                  </td>
                  <td>{paciente.cpf}</td>
                  <td>
                    {paciente.telefone && (
                      <div className={styles.contactInfo}>
                        <FiPhone className={styles.contactIcon} />
                        {paciente.telefone}
                      </div>
                    )}
                  </td>
                  <td>
                    {paciente.email && (
                      <div className={styles.contactInfo}>
                        <FiMail className={styles.contactIcon} />
                        {paciente.email}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className={styles.actions}>
                      {canEdit && (
                        <button
                          onClick={() => handleEdit(paciente)}
                          className={styles.editButton}
                          title="Editar"
                        >
                          <FiEdit2 />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(paciente.id)}
                          className={styles.deleteButton}
                          title="Excluir"
                        >
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className={styles.emptyState}>
            <FiUser className={styles.emptyIcon} />
            <h3>Nenhum paciente encontrado</h3>
            <p>Comece cadastrando seu primeiro paciente</p>
            <button
              onClick={() => setShowForm(true)}
              className={styles.addButton}
            >
              <FiPlus /> Cadastrar Paciente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pacientes;